import { type Handle, type RemixNode, css } from 'remix/ui'
import { NotFoundPage } from '#client/not-found-page.tsx'
import {
	BLOG_AUTHOR_NAME,
	BLOG_PLACEHOLDER_CALLOUT,
	formatBlogPostDate,
} from '#universal/blog-display.ts'
import { type BlogPostLoaderData } from '#universal/loader-data.ts'
import { landingArtAttrs } from '#universal/landing-images.ts'
import { routes } from '#universal/routes.ts'
import { renderMarkdownNodes } from '#client/markdown-view.tsx'
import { readCurrentRouterHref } from '#client/client-router.tsx'
import {
	createRouteData,
	renderRoutePendingStatus,
} from '#client/route-data.tsx'
import {
	routeLoaderRedirect,
	type RouteLoaderResult,
} from '#client/route-loader.ts'
import { readRouterPathname } from '#client/router-location.tsx'
import { readJson } from '#client/routes/account-approval-shared.ts'
import { publicSignupPrimaryCta } from '#universal/public-signup-copy.ts'
import {
	colors,
	radius,
	transitions,
	typography,
} from '#universal/styles/tokens.ts'
import {
	getPillButtonCss,
	pageHeadCss,
	proseCss,
} from '#universal/styles/style-primitives.ts'
import { getSlugFromPathname } from './blog-post-path.ts'

/**
 * Blog post, ported from the redesign prototype (`landing/blog-post.html`).
 * A 43rem editorial measure: back link → post head (display title + meta,
 * page-open rise) → optional headline image → optional AI-placeholder
 * callout → `.prose` body rendered from the server's markdown catalog →
 * quiet foot (read-next pointer + Kody greeting + signup button). Nothing
 * here hardcodes post content — body, dates, artwork, and the read-next
 * pointer all come from the blog API. The placeholder callout is per-post
 * frontmatter (`placeholder`, default true) until a human review turns it
 * off.
 */

function isBlogPostPath(href: string) {
	return (
		getSlugFromPathname(new URL(href, 'http://localhost').pathname) !== null
	)
}

export async function blogPostRouteLoader(
	url: URL,
	signal: AbortSignal,
): Promise<RouteLoaderResult> {
	const slug = getSlugFromPathname(url.pathname)
	if (!slug) {
		// Non-post paths under /blog (rss.xml, .json APIs) are served by the
		// worker as raw documents; leave the SPA instead of rendering a
		// missing-post page.
		return routeLoaderRedirect(`${url.pathname}${url.search}`)
	}

	const response = await fetch(routes.blogPostApi.href({ slug }), {
		headers: { Accept: 'application/json' },
		signal,
	})
	if (response.status === 404) {
		throw new Error('Blog post not found.')
	}
	const payload = await readJson<BlogPostLoaderData>(response)
	if (!response.ok || !payload?.ok) {
		throw new Error('Unable to load blog post.')
	}
	return {
		blogPost: payload,
	}
}

export function BlogPostRoute(handle: Handle) {
	const postData = createRouteData({
		key: 'blogPost',
		async load(href, signal) {
			const slug = getSlugFromPathname(
				new URL(href, 'http://localhost').pathname,
			)
			if (!slug) return null
			const response = await fetch(routes.blogPostApi.href({ slug }), {
				headers: { Accept: 'application/json' },
				signal,
			})
			if (response.status === 404) return null
			const payload = await readJson<BlogPostLoaderData>(response)
			if (!response.ok || !payload?.ok) {
				throw new Error('Unable to load blog post.')
			}
			return payload
		},
	})

	// Re-lexing markdown on every handle.update() would be wasted work; cache
	// the rendered body per markdown string (same policy as MarkdownView).
	let renderedForBody: string | null = null
	let renderedBody: Array<RemixNode> = []

	function renderPostBody(post: BlogPostLoaderData) {
		if (renderedForBody !== post.body) {
			renderedForBody = post.body
			// First-party prose: headings keep their authored h2 rhythm and
			// Kent's outbound links are not tagged as user-generated content.
			renderedBody = renderMarkdownNodes(post.body, {
				headingOffset: 0,
				linkRel: 'noopener noreferrer',
				headingIds: true,
				fences: post.bodyFences,
			})
		}
		return renderedBody
	}

	return () => {
		const currentHref = readCurrentRouterHref(handle)
		const slug = getSlugFromPathname(readRouterPathname(handle))
		if (!slug || !isBlogPostPath(currentHref)) {
			return <article mix={css(postCss)} />
		}

		const snapshot = postData.read(handle, currentHref)
		const signedOutCta = publicSignupPrimaryCta()

		if (snapshot.kind === 'not-found') {
			return <NotFoundPage />
		}

		const post = snapshot.data
		const pending = snapshot.kind === 'pending'

		return (
			<article mix={css(postCss)} aria-busy={pending ? 'true' : undefined}>
				<a
					data-rise
					style={{ '--rise': '0' }}
					href={routes.blog.href()}
					mix={css(postBackCss)}
				>
					← All posts
				</a>

				{snapshot.kind === 'error' ? (
					<p mix={css(postStatusCss)} role="status">
						Unable to load this blog post.
					</p>
				) : null}
				{/* Only the SPA cold path has no previous post to keep on screen. */}
				{pending && post === null ? (
					<p mix={css(postStatusCss)} role="status">
						Loading post…
					</p>
				) : null}
				{pending && post !== null ? renderRoutePendingStatus() : null}
				{post !== null ? (
					<>
						<header mix={css(postHeadCss)}>
							<h1 data-rise style={{ '--rise': '1' }}>
								{post.title}
							</h1>
							<p data-rise style={{ '--rise': '2' }} mix={css(postMetaCss)}>
								{BLOG_AUTHOR_NAME} · {formatBlogPostDate(post.date)}
							</p>
						</header>

						{post.image && post.imageAlt ? (
							<img
								src={post.image}
								alt={post.imageAlt}
								width={1600}
								height={900}
								loading="eager"
								decoding="async"
								data-rise
								style={{ '--rise': '3' }}
								mix={css(postImageCss)}
							/>
						) : null}

						{post.placeholder ? (
							<aside
								data-rise
								style={{ '--rise': '4' }}
								mix={css(placeholderCalloutCss)}
								role="note"
							>
								<p>{BLOG_PLACEHOLDER_CALLOUT}</p>
							</aside>
						) : null}

						<div mix={css(proseCss)}>{renderPostBody(post)}</div>

						<footer mix={css(postFootCss)}>
							{post.readNext ? (
								<a
									href={routes.blogPost.href({ slug: post.readNext.slug })}
									mix={css(readNextCss)}
								>
									<span mix={css(postMetaCss)}>Read next</span>
									<strong>{post.readNext.title}</strong>
								</a>
							) : null}
							<div mix={css(postCtaCss)}>
								<img
									{...landingArtAttrs('kody-greeting')}
									width={480}
									height={480}
									alt=""
								/>
								<p>
									Give your assistant a home of its own. Create a free account
									and start saving packages.
								</p>
								<a href={signedOutCta.href} mix={css(postCtaButtonCss)}>
									{signedOutCta.label}
								</a>
							</div>
						</footer>
					</>
				) : null}
			</article>
		)
	}
}

const postCss = {
	maxWidth: '43rem',
	marginInline: 'auto',
	padding:
		'clamp(2.5rem, 6vw, 4rem) clamp(1.25rem, 4vw, 2.5rem) clamp(4rem, 8vw, 6.5rem)',
}

const postBackCss = {
	display: 'inline-flex',
	alignItems: 'center',
	gap: '0.4rem',
	fontSize: '0.95rem',
	fontWeight: 550,
	color: colors.primaryText,
	textDecoration: 'none',
	'&:hover': {
		color: colors.text,
	},
}

/* The shared shirt-pattern whisper drifts behind the head, pulled toward the
   reading edge (the post head is left-aligned, unlike the centered page
   head). */
const postHeadCss = {
	position: 'relative' as const,
	// Borrows `pageHeadCss`'s `zIndex: -1` pseudo without the rest of it, so it
	// has to carry the `isolate` that scopes that negative z-index to this
	// element. `blogHeadCss` and timeline's `headCss` spread all of
	// `pageHeadCss` and inherit it for free.
	isolation: 'isolate' as const,
	marginTop: '1.8rem',
	'&::before': {
		...pageHeadCss['&::before'],
		inset: '-90% -30% -250%',
		background: `radial-gradient(ellipse 46% 55% at 70% 30%, oklch(from ${colors.text} l c h / 0.05), transparent 72%)`,
	},
	'& h1': {
		margin: 0,
		fontSize: 'clamp(2.1rem, 5vw, 3rem)',
		fontWeight: 760,
		letterSpacing: '-0.028em',
		lineHeight: 1.06,
		textWrap: 'balance' as const,
	},
	'& > p': {
		marginTop: '0.9rem',
	},
}

const postMetaCss = {
	margin: 0,
	color: colors.textMuted,
	fontSize: '0.88rem',
}

const postImageCss = {
	display: 'block',
	width: '100%',
	height: 'auto',
	// HTML width/height stay a 16:9 reservation for older posts; let the
	// file's intrinsic ratio win so square art is not stretched.
	aspectRatio: 'auto',
	margin: 'clamp(1.5rem, 4vw, 2.2rem) 0 0',
	borderRadius: radius.card,
	border: `1px solid ${colors.border}`,
}

const postStatusCss = {
	margin: 'clamp(1.8rem, 4vw, 2.5rem) 0 0',
	color: colors.textMuted,
	fontSize: '0.98rem',
}

const placeholderCalloutCss = {
	marginTop: 'clamp(1.4rem, 3vw, 2rem)',
	padding: '1rem 1.15rem',
	border: `1.5px solid ${colors.border}`,
	borderRadius: radius.card,
	backgroundColor: colors.surface,
	'& p': {
		margin: 0,
		color: colors.textMuted,
		fontSize: '0.95rem',
		lineHeight: 1.5,
		maxWidth: '62ch',
		textWrap: 'pretty' as const,
	},
}

const postFootCss = {
	marginTop: 'clamp(3rem, 7vw, 4.5rem)',
	paddingTop: 'clamp(1.8rem, 4vw, 2.5rem)',
	borderTop: `1px solid ${colors.border}`,
}

const readNextCss = {
	display: 'block',
	textDecoration: 'none',
	color: 'inherit',
	'& span': {
		display: 'block',
	},
	'& strong': {
		display: 'block',
		marginTop: '0.3rem',
		font: `720 1.25rem/1.25 ${typography.fontFamilyDisplay}`,
		letterSpacing: '-0.014em',
		transition: `color ${transitions.fast}`,
	},
	'&:hover strong': {
		color: colors.primaryText,
	},
}

const postCtaCss = {
	marginTop: 'clamp(2rem, 5vw, 3rem)',
	display: 'flex',
	alignItems: 'center',
	gap: '1.2rem',
	flexWrap: 'wrap' as const,
	'& img': {
		width: '76px',
		height: '76px',
		flex: 'none',
	},
	'& p': {
		flex: 1,
		minWidth: 0,
		margin: 0,
		color: colors.textMuted,
		fontSize: '0.98rem',
	},
}

const postCtaButtonCss = {
	...getPillButtonCss(),
	fontSize: '0.95rem',
	padding: '0.8rem 1.35rem',
}
